<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  dismissReport,
  getReportDetail,
  listReports,
  resolveReport,
  type ReportDetail,
  type ReportListItem,
  type ReportStatus,
  type ReportTargetSnapshot,
  type ReportTargetType,
} from '../api';

type TagType = 'success' | 'warning' | 'info' | 'danger';

const REPORT_TARGET_META: Record<ReportTargetType, { label: string; tag: TagType; canTakedown: boolean }> = {
  POST: { label: '帖子', tag: 'success', canTakedown: true },
  COMMENT: { label: '评论', tag: 'warning', canTakedown: true },
  USER: { label: '用户', tag: 'info', canTakedown: false },
  MESSAGE: { label: '消息', tag: 'danger', canTakedown: false },
};

const REPORT_STATUS_META: Record<ReportStatus, { label: string; className: string }> = {
  PENDING: { label: '待处理', className: 'st-pending' },
  RESOLVED: { label: '已处理', className: 'st-resolved' },
  DISMISSED: { label: '已驳回', className: 'st-dismissed' },
};

const tab = ref<ReportStatus>('PENDING');
const loading = ref(false);
const detailLoading = ref(false);
const submitting = ref(false);
const list = ref<ReportListItem[]>([]);
const current = ref<ReportDetail | null>(null);
const selectedId = ref<string | null>(null);
let listSeq = 0;
let detailSeq = 0;

async function reload() {
  const seq = ++listSeq;
  const status = tab.value;
  loading.value = true;
  list.value = [];
  current.value = null;
  selectedId.value = null;
  detailLoading.value = false;
  try {
    const next = await listReports(status);
    if (seq !== listSeq || status !== tab.value) return;
    list.value = next;
  } catch (e) {
    if (seq === listSeq) ElMessage.error(normalizeError(e));
  } finally {
    if (seq === listSeq) loading.value = false;
  }
}

async function openDetail(item: ReportListItem) {
  const seq = ++detailSeq;
  selectedId.value = item.id;
  current.value = null;
  detailLoading.value = true;
  try {
    const detail = await getReportDetail(item.id);
    if (seq !== detailSeq || selectedId.value !== item.id) return;
    current.value = detail;
  } catch (e) {
    if (seq === detailSeq) ElMessage.error(normalizeError(e));
  } finally {
    if (seq === detailSeq) detailLoading.value = false;
  }
}

async function onResolve(takedown: boolean) {
  const item = current.value;
  if (!item || submitting.value) return;

  const config = takedown
    ? {
        title: '认定违规并下架',
        message: '确认将该举报标记为已处理,并尝试下架目标内容?帖子会置为 BLOCKED,评论会被软删除。',
        confirmButtonText: '处理并下架',
        type: 'warning' as const,
      }
    : {
        title: '认定违规',
        message: '确认将该举报标记为已处理?本操作不会下架目标内容。',
        confirmButtonText: '确认处理',
        type: 'success' as const,
      };

  try {
    await ElMessageBox.confirm(config.message, config.title, {
      confirmButtonText: config.confirmButtonText,
      cancelButtonText: '取消',
      type: config.type,
    });
  } catch {
    return;
  }

  submitting.value = true;
  try {
    const r = await resolveReport(item.id, takedown);
    if (!takedown) {
      ElMessage.success('已标记为违规处理');
    } else if (r.takedown) {
      ElMessage.success('已处理并下架目标内容');
    } else {
      ElMessage.warning('已处理,但目标未实际下架。可能已不存在或该类型不支持下架。');
    }
    removeHandledItem(item.id);
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    submitting.value = false;
  }
}

async function onDismiss() {
  const item = current.value;
  if (!item || submitting.value) return;
  try {
    await ElMessageBox.confirm('确认驳回该举报?将认为当前举报不成立。', '驳回举报', {
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
      type: 'warning',
    });
  } catch {
    return;
  }

  submitting.value = true;
  try {
    await dismissReport(item.id);
    ElMessage.success('已驳回举报');
    removeHandledItem(item.id);
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    submitting.value = false;
  }
}

function removeHandledItem(id: string) {
  list.value = list.value.filter((item) => item.id !== id);
  if (current.value?.id === id) current.value = null;
  if (selectedId.value === id) selectedId.value = null;
}

function normalizeError(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null | undefined): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function targetLabel(t: ReportTargetType): string {
  return REPORT_TARGET_META[t].label;
}

function targetTagType(t: ReportTargetType): TagType {
  return REPORT_TARGET_META[t].tag;
}

function statusLabel(s: ReportStatus): string {
  return REPORT_STATUS_META[s].label;
}

function statusClass(s: ReportStatus): string {
  return REPORT_STATUS_META[s].className;
}

function canTakedown(item: ReportListItem): boolean {
  return REPORT_TARGET_META[item.targetType].canTakedown;
}

function shortId(id: string): string {
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function reasonPreview(reason: string): string {
  return reason.length > 42 ? `${reason.slice(0, 42)}…` : reason;
}

function targetMissingText(target: ReportTargetSnapshot): string {
  if (target.exists) return '';
  if (target.unsupportedReason) return target.unsupportedReason;
  return '目标不存在或已无法读取';
}

onMounted(() => {
  reload();
});
</script>

<template>
  <div class="audit-pane">
    <el-tabs v-model="tab" @tab-change="reload" class="tabs">
      <el-tab-pane label="待处理" name="PENDING" />
      <el-tab-pane label="已处理" name="RESOLVED" />
      <el-tab-pane label="已驳回" name="DISMISSED" />
    </el-tabs>

    <div class="layout">
      <div class="list" v-loading="loading">
        <el-empty v-if="!list.length && !loading" description="暂无举报" />
        <div
          v-for="item of list"
          :key="item.id"
          class="list-item"
          :class="{ active: selectedId === item.id }"
          @click="openDetail(item)"
        >
          <div class="list-row">
            <el-tag size="small" :type="targetTagType(item.targetType)">
              {{ targetLabel(item.targetType) }}
            </el-tag>
            <span class="report-status" :class="statusClass(item.status)">
              {{ statusLabel(item.status) }}
            </span>
          </div>
          <div class="list-sub">
            目标 <code>{{ shortId(item.targetId) }}</code>
          </div>
          <div class="list-reason">{{ reasonPreview(item.reason) }}</div>
          <div class="list-sub">
            举报人 {{ item.reporter?.displayName || '未知用户' }} · {{ formatTime(item.createdAt) }}
          </div>
        </div>
      </div>

      <div class="detail" v-loading="detailLoading">
        <div v-if="!current" class="detail-empty">
          <el-empty :description="detailLoading ? '正在加载举报详情' : '选一条举报查看详情'" />
        </div>
        <template v-else>
          <div class="detail-header">
            <div class="title">
              {{ targetLabel(current.targetType) }}举报
              <el-tag :type="targetTagType(current.targetType)">{{ current.targetType }}</el-tag>
              <span class="report-status" :class="statusClass(current.status)">
                {{ statusLabel(current.status) }}
              </span>
            </div>
            <div class="subtitle">举报 ID <code>{{ current.id }}</code></div>
            <div class="subtitle">目标 ID <code>{{ current.targetId }}</code></div>
            <div class="subtitle">
              创建时间 {{ formatTime(current.createdAt) }} · 处理时间 {{ formatTime(current.handledAt) }}
            </div>
          </div>

          <div class="section">
            <div class="section-title">被举报目标</div>
            <el-alert
              v-if="!current.target.exists"
              type="warning"
              :closable="false"
              :title="targetMissingText(current.target)"
            />

            <div v-else-if="current.target.type === 'POST'" class="target-box">
              <div class="target-meta">
                <el-tag size="small">{{ current.target.status }}</el-tag>
                <span>作者:{{ current.target.author?.displayName || '未知用户' }}</span>
                <span>创建于 {{ formatTime(current.target.createdAt) }}</span>
                <span v-if="current.target.deletedAt">已删除:{{ formatTime(current.target.deletedAt) }}</span>
              </div>
              <div class="target-text">{{ current.target.text }}</div>
              <div v-if="current.target.media.length" class="media-list">
                <a v-for="m of current.target.media" :key="m.id" :href="m.url" target="_blank" rel="noreferrer">
                  媒体 {{ m.sortOrder + 1 }}
                </a>
              </div>
              <div class="target-sub">点赞 {{ current.target.likeCount }} · 评论 {{ current.target.commentCount }}</div>
            </div>

            <div v-else-if="current.target.type === 'COMMENT'" class="target-box">
              <div class="target-meta">
                <span>评论作者:{{ current.target.author?.displayName || '未知用户' }}</span>
                <span>创建于 {{ formatTime(current.target.createdAt) }}</span>
                <span v-if="current.target.deletedAt">已删除:{{ formatTime(current.target.deletedAt) }}</span>
              </div>
              <div class="target-text">{{ current.target.text }}</div>
              <div v-if="current.target.post" class="parent-post">
                <div class="section-title">所在帖子</div>
                <div class="target-meta">
                  <el-tag size="small">{{ current.target.post.status }}</el-tag>
                  <span>作者:{{ current.target.post.author?.displayName || '未知用户' }}</span>
                </div>
                <div class="target-text small">{{ current.target.post.text }}</div>
              </div>
            </div>

            <div v-else-if="current.target.type === 'USER'" class="target-box">
              <div class="target-meta">
                <el-tag size="small">{{ current.target.status }}</el-tag>
                <span>{{ current.target.displayName || '未设置昵称' }}</span>
                <span v-if="current.target.schoolName">{{ current.target.schoolName }}</span>
                <span v-if="current.target.enrollYear">{{ current.target.enrollYear }}届</span>
              </div>
              <div class="target-sub">用户 ID <code>{{ current.target.id }}</code></div>
              <div class="target-sub">注册于 {{ formatTime(current.target.createdAt) }}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">举报原因</div>
            <div class="reason-box">{{ current.reason }}</div>
          </div>

          <div class="section">
            <div class="section-title">举报人</div>
            <div class="meta-grid">
              <span>昵称</span>
              <strong>{{ current.reporter?.displayName || '未知用户' }}</strong>
              <span>ID</span>
              <code>{{ current.reporter?.id || '-' }}</code>
            </div>
          </div>

          <div v-if="current.status === 'PENDING'" class="detail-actions">
            <el-button
              type="success"
              size="large"
              :loading="submitting"
              :disabled="submitting || detailLoading"
              @click="onResolve(false)"
            >
              认定违规
            </el-button>
            <el-tooltip
              :disabled="canTakedown(current)"
              content="当前后端仅支持帖子/评论下架,用户处罚走总后台,消息暂无下架语义。"
              placement="top"
            >
              <span>
                <el-button
                  type="warning"
                  size="large"
                  :loading="submitting"
                  :disabled="submitting || detailLoading || !canTakedown(current)"
                  @click="onResolve(true)"
                >
                  认定违规并下架
                </el-button>
              </span>
            </el-tooltip>
            <el-button
              type="danger"
              size="large"
              :loading="submitting"
              :disabled="submitting || detailLoading"
              @click="onDismiss"
            >
              驳回举报
            </el-button>
          </div>
          <div v-else class="detail-handled">
            <span v-if="current.status === 'RESOLVED'" class="tag-resolved">✓ 已处理</span>
            <span v-else class="tag-dismissed">✕ 已驳回</span>
            <div class="muted">处理时间:{{ formatTime(current.handledAt) }}</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audit-pane { display: flex; flex-direction: column; height: calc(100vh - 80px); }
.tabs { margin-bottom: 12px; }
.layout { display: flex; flex: 1; gap: 16px; overflow: hidden; }
.list { width: 380px; overflow-y: auto; background: #fff; border-radius: 8px; padding: 8px; }
.list-item { padding: 12px; border-radius: 6px; cursor: pointer; margin-bottom: 6px; border: 1px solid transparent; }
.list-item:hover { background: #f5f5f7; }
.list-item.active { background: #e6f4ff; border-color: #409eff; }
.list-row { display: flex; justify-content: space-between; align-items: center; }
.list-sub { margin-top: 6px; font-size: 12px; color: #86868b; }
.list-sub code { background: #f5f5f7; padding: 1px 5px; border-radius: 4px; }
.list-reason { margin-top: 8px; color: #1d1d1f; line-height: 1.45; font-size: 14px; }

.report-status { font-size: 12px; padding: 1px 8px; border-radius: 10px; font-weight: 500; }
.st-pending { background: #fff4e0; color: #b26a00; }
.st-resolved { background: #e3f6e9; color: #1e7c3e; }
.st-dismissed { background: #fdeaea; color: #b42020; }

.detail { flex: 1; overflow-y: auto; background: #fff; border-radius: 8px; padding: 24px; }
.detail-empty { height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-header .title { font-size: 22px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
.detail-header .subtitle { margin-top: 6px; color: #666; font-size: 13px; }
.detail-header .subtitle code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; }

.section { margin-top: 24px; }
.section-title { font-size: 14px; color: #86868b; margin-bottom: 6px; }
.reason-box,
.target-box {
  background: #f5f5f7;
  border-radius: 6px;
  padding: 16px;
  white-space: pre-wrap;
  line-height: 1.6;
  font-size: 14px;
}
.target-meta { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; color: #666; font-size: 13px; }
.target-text { margin-top: 10px; color: #1d1d1f; white-space: pre-wrap; }
.target-text.small { color: #555; }
.target-sub { margin-top: 8px; color: #86868b; font-size: 13px; }
.media-list { margin-top: 10px; display: flex; gap: 10px; flex-wrap: wrap; }
.media-list a { color: #1677ff; text-decoration: none; }
.parent-post { margin-top: 14px; border-top: 1px solid #e4e7ed; padding-top: 12px; }
.meta-grid { display: grid; grid-template-columns: 80px 1fr; row-gap: 8px; column-gap: 12px; font-size: 14px; }
.meta-grid span { color: #86868b; }
.meta-grid code,
.target-sub code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; width: fit-content; }

.detail-actions { margin-top: 32px; display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.detail-handled { margin-top: 24px; }
.tag-resolved { color: #1e7c3e; font-size: 18px; font-weight: 600; }
.tag-dismissed { color: #b42020; font-size: 16px; }
.muted { color: #86868b; font-size: 13px; margin-top: 6px; }
</style>
